import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  smartphoneTabletRepairs, 
  computerRepairs, 
  otherDevicesRepairs,
  accessoiresCategories,
  servicesDigitaux 
} from '../data/servicesData';

export const ServicesComplete = () => {
  const [activeTab, setActiveTab] = useState('reparations-mobile');
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const scrollToContact = () => {
    const element = document.getElementById('quote');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="pt-32 pb-20 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 sm:mb-10">
            Services de Réparation à Villeurbanne & Lyon Métropole
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12 sm:mb-16">
            Réparations professionnelles de smartphones, tablettes & PC avec le meilleur rapport qualité-prix du marché — stock immédiat, intervention rapide.
          </p>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-12 sm:mt-16">
          <div className="relative z-10 bg-white pb-28 sm:pb-10">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 bg-white p-2 rounded-2xl mx-auto border-0 shadow-lg">
              <TabsTrigger value="reparations-mobile" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white font-extrabold text-base sm:text-lg rounded-xl py-4 px-3 flex items-center justify-center">
                📱 Mobiles & Tablettes
              </TabsTrigger>
              <TabsTrigger value="reparations-pc" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white font-extrabold text-base sm:text-lg rounded-xl py-4 px-3 flex items-center justify-center">
                💻 Ordinateurs
              </TabsTrigger>
              <TabsTrigger value="autres-appareils" className="data-[state=active]:bg-green-500 data-[state=active]:text-white font-extrabold text-base sm:text-lg rounded-xl py-4 px-3 flex items-center justify-center">
                🎮 Consoles & Autres
              </TabsTrigger>
              <TabsTrigger value="accessoires" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white font-extrabold text-base sm:text-lg rounded-xl py-4 px-3 flex items-center justify-center">
                🛒 Accessoires
              </TabsTrigger>
              <TabsTrigger value="services-digitaux" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white font-extrabold text-base sm:text-lg rounded-xl py-4 px-3 flex items-center justify-center">
                💳 Services
              </TabsTrigger>
            </TabsList>
          </div>

          {/* RÉPARATIONS SMARTPHONES & TABLETTES */}
          <TabsContent value="reparations-mobile" className="mt-8">
            <div className="bg-blue-50/30 rounded-2xl p-6 shadow-lg border-2 border-blue-100">
              <div className="space-y-2">
                {smartphoneTabletRepairs.map((repair) => (
                  <div key={repair.id} className="border-b border-gray-200 last:border-0">
                    <button
                      onClick={() => toggleCategory(repair.id)}
                      className="w-full flex items-center justify-between py-4 bg-blue-50 hover:bg-blue-100 transition-colors px-4 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {expandedCategories[repair.id] ? (
                          <ChevronDown className="text-blue-600" size={20} />
                        ) : (
                          <ChevronRight className="text-gray-400" size={20} />
                        )}
                        <div className="text-left">
                          <h4 className="font-bold text-gray-900">{repair.category}</h4>
                          <p className="text-sm text-gray-500">{repair.description}</p>
                        </div>
                      </div>
                    </button>
                    
                    {expandedCategories[repair.id] && (
                      <div className="px-4 pb-4 ml-8">
                        <ul className="space-y-2">
                          {repair.services.map((service, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-gray-700">
                              <span className="text-blue-600 mt-1">•</span>
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={scrollToContact}
                          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                        >
                          Demander un devis rapide →
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">📱 Réparations Smartphones & Tablettes</h3>
                <p className="text-gray-600">Toutes marques : Apple, Samsung, Xiaomi, Oppo, Vivo, Motorola, LG, POCO, ASUS, Lenovo, OnePlus, Pixel...</p>
              </div>
            </div>
          </TabsContent>

          {/* RÉPARATIONS ORDINATEURS */}
          <TabsContent value="reparations-pc" className="mt-8">
            <div className="bg-purple-50/30 rounded-2xl p-6 shadow-lg border-2 border-purple-100">
              <div className="space-y-2">
                {computerRepairs.map((repair) => (
                  <div key={repair.id} className="border-b border-gray-200 last:border-0">
                    <button
                      onClick={() => toggleCategory(`pc-${repair.id}`)}
                      className="w-full flex items-center justify-between py-4 bg-purple-50 hover:bg-purple-100 transition-colors px-4 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {expandedCategories[`pc-${repair.id}`] ? (
                          <ChevronDown className="text-purple-600" size={20} />
                        ) : (
                          <ChevronRight className="text-gray-400" size={20} />
                        )}
                        <div className="text-left">
                          <h4 className="font-bold text-gray-900">{repair.category}</h4>
                          <p className="text-sm text-gray-500">{repair.description}</p>
                        </div>
                      </div>
                    </button>
                    
                    {expandedCategories[`pc-${repair.id}`] && (
                      <div className="px-4 pb-4 ml-8">
                        <ul className="space-y-2">
                          {repair.services.map((service, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-gray-700">
                              <span className="text-purple-600 mt-1">•</span>
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={scrollToContact}
                          className="mt-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                        >
                          Demander un devis rapide →
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">💻 Réparations Ordinateurs</h3>
                <p className="text-gray-600">PC portables et fixes : Apple MacBook, Asus, Lenovo, HP, Dell, Acer, MSI...</p>
              </div>
            </div>
          </TabsContent>

          {/* AUTRES APPAREILS */}
          <TabsContent value="autres-appareils" className="mt-8">
            <div className="bg-green-50/30 rounded-2xl p-6 shadow-lg border-2 border-green-100">
              <div className="space-y-2">
                {otherDevicesRepairs.map((device) => (
                  <div key={device.id} className="border-b border-gray-200 last:border-0">
                    <button
                      onClick={() => toggleCategory(`other-${device.id}`)}
                      className="w-full flex items-center justify-between py-4 bg-green-50 hover:bg-green-100 transition-colors px-4 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {expandedCategories[`other-${device.id}`] ? (
                          <ChevronDown className="text-green-600" size={20} />
                        ) : (
                          <ChevronRight className="text-gray-400" size={20} />
                        )}
                        <div className="text-left">
                          <h4 className="font-bold text-gray-900">{device.category}</h4>
                          <p className="text-sm text-gray-500">{device.description}</p>
                        </div>
                      </div>
                    </button>
                    
                    {expandedCategories[`other-${device.id}`] && (
                      <div className="px-4 pb-4 ml-8">
                        <ul className="space-y-2">
                          {device.services.map((service, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-gray-700">
                              <span className="text-green-600 mt-1">•</span>
                              <span>{service}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={scrollToContact}
                          className="mt-4 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                        >
                          Demander un devis rapide →
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">🎮 Consoles & Appareils High-Tech</h3>
                <p className="text-gray-600">PlayStation, Xbox, Nintendo Switch, montres connectées, manettes...</p>
              </div>
            </div>
          </TabsContent>

          {/* ACCESSOIRES */}
          <TabsContent value="accessoires" className="mt-8">
            <div className="bg-orange-50/30 rounded-2xl p-6 shadow-lg border-2 border-orange-100">
              <div className="space-y-2">
                {accessoiresCategories.map((cat) => (
                  <div key={cat.id} className="border-b border-gray-200 last:border-0">
                    <button
                      onClick={() => toggleCategory(`acc-${cat.id}`)}
                      className="w-full flex items-center justify-between py-4 bg-orange-50 hover:bg-orange-100 transition-colors px-4 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {expandedCategories[`acc-${cat.id}`] ? (
                          <ChevronDown className="text-orange-600" size={20} />
                        ) : (
                          <ChevronRight className="text-gray-400" size={20} />
                        )}
                        <h4 className="font-bold text-gray-900 text-left">{cat.category}</h4>
                      </div>
                    </button>
                    
                    {expandedCategories[`acc-${cat.id}`] && (
                      <div className="px-4 pb-4 ml-8">
                        <ul className="space-y-2">
                          {cat.products.map((product, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-gray-700">
                              <span className="text-orange-600 mt-1">•</span>
                              <span>{product}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">🛒 Vente d'Accessoires</h3>
                <p className="text-gray-600">Large gamme d'accessoires pour tous vos appareils</p>
              </div>
            </div>
          </TabsContent>

          {/* SERVICES DIGITAUX */}
          <TabsContent value="services-digitaux" className="mt-8">
            <div className="bg-pink-50/30 rounded-2xl p-6 shadow-lg border-2 border-pink-100">
              <div className="space-y-2">
                {servicesDigitaux.map((service) => (
                  <div key={service.id} className="border-b border-gray-200 last:border-0">
                    <button
                      onClick={() => toggleCategory(`digital-${service.id}`)}
                      className="w-full flex items-center justify-between py-4 bg-pink-50 hover:bg-pink-100 transition-colors px-4 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {expandedCategories[`digital-${service.id}`] ? (
                          <ChevronDown className="text-pink-600" size={20} />
                        ) : (
                          <ChevronRight className="text-gray-400" size={20} />
                        )}
                        <h4 className="font-bold text-gray-900 text-left">{service.category}</h4>
                      </div>
                    </button>
                    
                    {expandedCategories[`digital-${service.id}`] && (
                      <div className="px-4 pb-4 ml-8">
                        <ul className="space-y-2">
                          {service.services.map((item, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-gray-700">
                              <span className="text-pink-600 mt-1">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">💳 Services Dématérialisés</h3>
                <p className="text-gray-600">Recharges, forfaits, transferts internationaux et cartes cadeaux</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h3 className="text-3xl font-bold mb-4">
            Vous ne trouvez pas ce que vous cherchez ?
          </h3>
          <p className="text-xl text-blue-100 mb-8">
            Contactez-nous ! Nous proposons de nombreux autres services et produits.
          </p>
          <button
            onClick={scrollToContact}
            className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 shadow-lg"
          >
            Nous contacter
          </button>
        </div>
      </div>
    </section>
  );
};
